import React, { useRef, useCallback } from 'react';
import { Field, useFormikContext, ErrorMessage } from 'formik';

import styles from './CustomField.module.scss';
import { ToolTip } from '../Form';
import { WordCount } from '@/utils/common';

type TextAreaPropsType = {
  name: string;
  placeholder?: string;
  label: string;
  isRequired?: boolean;
  wrapperClassname?: string;
  hasTooltip?: boolean;
  tooltipHeading?: string;
  tooltipContent?: string;
  grayPlaceholder?: boolean;
  isDisable?: boolean;
  tooltipClassname?: string;
  wordCount?: number;
  value?: string;
  error?: string | undefined;
  touched?: boolean | undefined;
};

const TextAreaField = ({
  name,
  placeholder,
  label,
  isRequired,
  wrapperClassname,
  hasTooltip,
  tooltipContent,
  tooltipHeading,
  grayPlaceholder,
  isDisable,
  tooltipClassname,
  wordCount,
  value,
  error,
  touched,
}: TextAreaPropsType) => {
  const { setFieldValue } = useFormikContext();
  const keyCodeRef = useRef<string | undefined>(undefined);
  const onPasteRef = useRef(false);

  const handleChangeInput = useCallback(
    (inputValue: string) => {
      const limit = wordCount
        ? wordCount + inputValue.length - inputValue.replace(/(\r\n|\n|\r)/gm, '').length
        : undefined;

      if (limit === undefined) {
        setFieldValue(name, inputValue);
        return;
      }

      if (limit !== undefined) {
        if (inputValue.length <= limit) {
          setFieldValue(name, inputValue);
          return;
        } else {
          if (onPasteRef.current) {
            setFieldValue(name, inputValue.slice(0, limit));
            onPasteRef.current = false;
          }
          if (
            keyCodeRef.current &&
            (keyCodeRef.current === 'Backspace' || keyCodeRef.current === 'Delete')
          ) {
            setFieldValue(name, inputValue);
            return;
          }
        }
      }
    },
    [setFieldValue, name, wordCount],
  );

  return (
    <div className="lg:inline-block relative">
      <div
        className={`flex flex-wrap items-start ${wrapperClassname || 'mb-[20px]'} ${
          wordCount && value ? 'pb-[12px]' : ''
        }`}
      >
        <label
          htmlFor={name}
          className={`block text-[16px] leading-[24px] mr-[18px] w-full lg:w-[173px] mb-[4px] lg:mb-0 text-left lg:text-right lg:mt-[8px] shrink-0 ${
            isDisable ? 'text-gray4' : ''
          }`}
        >
          {label}
          {isRequired && <span className="text-red">*</span>}
        </label>
        <div className="flex flex-col w-full lg:w-[600px]">
          <Field
            as="textarea"
            name={name}
            placeholder={placeholder || ''}
            className={`${styles['input-field']} ${styles['text-area']} ${
              error && touched ? styles['error'] : ''
            } ${grayPlaceholder ? styles['gray-placeholder'] : ''} ${
              isDisable ? styles['field-disable'] : ''
            }`}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
              handleChangeInput(event.target.value);
            }}
            onKeyDown={(keyEvent: React.KeyboardEvent) => {
              keyCodeRef.current = keyEvent.code;
            }}
            onPaste={() => {
              onPasteRef.current = true;
            }}
          />
          <ErrorMessage
            component="span"
            name={name}
            className={`${styles['error-message']} leading-normal text-[12px]`}
          />
        </div>
      </div>
      {wordCount && value && (
        <h5
          className={`absolute top-[101px] lg:top-[101px] xs:top-[130px] right-0 transition-all leading-normal text-[12px] ${
            error && touched ? 'text-red' : 'text-gray4'
          }`}
        >
          {WordCount(value)}/{wordCount}
        </h5>
      )}

      {hasTooltip && (
        <ToolTip
          wrapperClassName={`absolute top-[0] right-[14px] lg:top-[10px] lg:right-[-47px] ${tooltipClassname}`}
          tooltipContent={tooltipContent}
          tooltipHeading={tooltipHeading}
        />
      )}
    </div>
  );
};

export default TextAreaField;
